import React from "react";
import type { Payment, RepairTicket } from "../types/pos";
import { useAuth } from "../../../context/AuthContext";
import { Select, MenuItem, TextField, Button, FormControl, InputLabel } from "@mui/material";

type Props = {
  payments: Payment[];
  setPayments: (p: Payment[]) => void;
  total: number;
  ticket?: RepairTicket | null;
};

const paymentMethods = [
  { value: "cash", label: "Cash" },
  { value: "card", label: "Card" },
  { value: "mobile", label: "Mobile Money" },
  { value: "bank", label: "Bank Transfer" },
  { value: "other", label: "Other" },
];

const PaymentPanel: React.FC<Props> = ({ payments, setPayments, total, ticket }) => {
  const [method, setMethod] = React.useState("");
  const [amount, setAmount] = React.useState<number | "">("");
  const [error, setError] = React.useState<string | null>(null);

  const { role } = useAuth();

  // Calculate totals
  const paid = payments.reduce((sum, p) => sum + p.amount, 0);
  const due = Math.max(0, total - paid);
  const change = paid > total ? paid - total : 0;

  const addPayment = () => {
    setError(null);
    if (!method) {
      setError("Select a payment method");
      return;
    }
    if (!amount || typeof amount !== "number" || amount <= 0) {
      setError("Enter a valid payment amount");
      return;
    }
    setPayments([...payments, { method, amount }]);
    setMethod("");
    setAmount("");
  };

  return (
    <div>
      <label className="block font-medium mb-1">Payments</label>
      {ticket && (
        <div className="mb-1 text-xs text-blue-700 bg-blue-100 px-2 py-1 rounded">
          Linked to ticket <b>{ticket.ticketNumber}</b> ({ticket.status})
        </div>
      )}

      {/* Payment Entry */}
      <div className="flex gap-2 mb-2 items-center">
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="payment-method-label">Method</InputLabel>
          <Select
            labelId="payment-method-label"
            value={method}
            label="Method"
            onChange={(e) => setMethod(e.target.value as string)}
            displayEmpty
          >
            <MenuItem value=""><em>Select Method</em></MenuItem>
            {paymentMethods.map((pm) => (
              <MenuItem key={pm.value} value={pm.value}>{pm.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          type="number"
          inputProps={{ min: 0, step: 0.01 }}
          placeholder="Amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ width: 120 }}
        />
        <Button
          variant="contained"
          color="success"
          onClick={addPayment}
          disabled={!method || !amount || (typeof amount === 'number' && amount <= 0)}
        >
          Add
        </Button>
      </div>
      {error && <div className="text-xs text-red-600 mb-1">{error}</div>}

      {/* Payments List */}
      <ul className="text-sm mb-1">
        {payments.map((p, i) => (
          <li key={i}>
            {paymentMethods.find(pm => pm.value === p.method)?.label ?? p.method}: <b>${p.amount.toFixed(2)}</b>
          </li>
        ))}
      </ul>

      {/* Totals */}
      <div className="flex flex-col gap-1 text-right mt-2">
        <span>
          <span className="font-semibold">Total:</span>{" "}
          <span className="text-blue-700">${total.toFixed(2)}</span>
        </span>
        <span>
          <span className="font-semibold">Paid:</span>{" "}
          <span className={paid >= total ? "text-green-700" : "text-orange-600"}>${paid.toFixed(2)}</span>
        </span>
        {due > 0 && (
          <span className="font-semibold text-red-700">
            Due: ${due.toFixed(2)}
          </span>
        )}
        {change > 0 && (
          <span className="font-bold text-green-700 text-lg">
            Change to Give: ${change.toFixed(2)}
          </span>
        )}
      </div>
      <div className="mt-2 text-xs text-gray-400">User Role: {role || "N/A"}</div>
    </div>
  );
};

export default PaymentPanel;
